import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useGeekMode } from '../contexts/GeekModeContext'
import { useWorkflow } from '../contexts/WorkflowContext'
import {
  getGeekModeExecutions,
  getGeekModeStats,
  getAIModels,
  updateAIModel,
  PromptExecution,
  AIModelProvider,
} from '../services/api'
import GeekModePromptEditor from './GeekModePromptEditor'
import ApertureStatusTab from './ApertureStatusTab'

type GeekModeTab = 'executions' | 'prompts' | 'settings' | 'aperture'

interface GeekModePanelProps {
  projectId?: string
  documentId?: string
}

export default function GeekModePanel({ projectId, documentId }: GeekModePanelProps) {
  const { isEnabled, isPanelOpen, togglePanel } = useGeekMode()
  const { setWorkflow, isPreSales } = useWorkflow()
  const [activeTab, setActiveTab] = useState<GeekModeTab>('executions')
  const [selectedExecution, setSelectedExecution] = useState<PromptExecution | null>(null)
  const [filter, setFilter] = useState<'all' | 'completed' | 'error'>('all')
  const [usePromptLibrary, setUsePromptLibrary] = useState(
    () => localStorage.getItem('geekmode-use-prompt-library') === 'true'
  )
  const panelRef = useRef<HTMLDivElement>(null)

  // Fetch executions
  const { data: executions = [], isLoading } = useQuery({
    queryKey: ['geekmode-executions', projectId, documentId],
    queryFn: async () => {
      const response = await getGeekModeExecutions({
        project_id: projectId,
        document_id: documentId,
        limit: 100,
      })
      return response.data
    },
    enabled: isEnabled && isPanelOpen,
    refetchInterval: 5000, // Refresh every 5 seconds for live updates
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['geekmode-stats', projectId, documentId],
    queryFn: async () => {
      const response = await getGeekModeStats({
        project_id: projectId,
        document_id: documentId,
      })
      return response.data
    },
    enabled: isEnabled && isPanelOpen,
    refetchInterval: 5000,
  })

  // Fetch AI models
  const queryClient = useQueryClient()
  const { data: aiModels, isLoading: aiModelsLoading } = useQuery({
    queryKey: ['geekmode-ai-models'],
    queryFn: async () => {
      const response = await getAIModels()
      return response.data
    },
    enabled: isEnabled && isPanelOpen && activeTab === 'settings',
  })

  // Update AI model mutation
  const updateModelMutation = useMutation({
    mutationFn: async (model: AIModelProvider) => {
      const response = await updateAIModel(model)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geekmode-ai-models'] })
    },
  })

  // Filter executions
  const filteredExecutions = executions.filter(exec => {
    if (filter === 'all') return true
    if (filter === 'completed') return exec.status === 'completed'
    if (filter === 'error') return exec.status === 'error'
    return true
  })

  // Auto-scroll to top when new executions come in
  useEffect(() => {
    if (panelRef.current && executions.length > 0) {
      panelRef.current.scrollTop = 0
    }
  }, [executions.length])

  if (!isEnabled || !isPanelOpen) {
    return null
  }

  const formatDuration = (ms?: number) => {
    if (!ms) return '---'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }

  const formatTokens = (input?: number, output?: number) => {
    if (!input && !output) return '---'
    return `${input?.toLocaleString() || 0} / ${output?.toLocaleString() || 0}`
  }

  const formatTimestamp = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-US', { hour12: false })
  }

  const truncateText = (text: string, maxLen: number) => {
    if (text.length <= maxLen) return text
    return text.slice(0, maxLen) + '...'
  }

  // Panel width based on active tab and selection state
  const getPanelWidth = () => {
    if (activeTab === 'prompts') return '700px'
    return selectedExecution ? '900px' : '450px'
  }

  return (
    <div
      className="fixed right-0 top-0 h-full z-40 flex"
      style={{
        width: getPanelWidth(),
        transition: 'width 0.3s ease',
      }}
    >
      {/* Detail Panel */}
      {selectedExecution && (
        <div
          className="w-[450px] h-full flex flex-col"
          style={{
            background: 'rgba(0, 10, 0, 0.98)',
            borderLeft: '1px solid rgba(0, 255, 0, 0.2)',
          }}
        >
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}
          >
            <div className="font-mono text-sm" style={{ color: '#00ff00' }}>
              <i className="fa-solid fa-code mr-2" />
              {selectedExecution.section_name || 'Execution Details'}
            </div>
            <button
              onClick={() => setSelectedExecution(null)}
              className="p-1 hover:opacity-70"
              style={{ color: '#00ff00' }}
            >
              <i className="fa-solid fa-times" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 font-mono text-xs">
            {/* Prompt Section */}
            <div className="mb-4">
              <div
                className="uppercase text-[10px] mb-2"
                style={{ color: 'rgba(0, 255, 0, 0.5)' }}
              >
                Prompt
              </div>
              <pre
                className="p-3 rounded whitespace-pre-wrap break-words"
                style={{
                  background: 'rgba(0, 255, 0, 0.05)',
                  border: '1px solid rgba(0, 255, 0, 0.1)',
                  color: 'rgba(0, 255, 0, 0.8)',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                {selectedExecution.prompt_text}
              </pre>
            </div>

            {/* Response Section */}
            <div className="mb-4">
              <div
                className="uppercase text-[10px] mb-2"
                style={{ color: 'rgba(0, 255, 0, 0.5)' }}
              >
                Response
              </div>
              <pre
                className="p-3 rounded whitespace-pre-wrap break-words"
                style={{
                  background: 'rgba(0, 255, 0, 0.05)',
                  border: '1px solid rgba(0, 255, 0, 0.1)',
                  color: 'rgba(0, 255, 0, 0.8)',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                {selectedExecution.response_text || 'No response'}
              </pre>
            </div>

            {/* Prompt Source Info */}
            {selectedExecution.prompt_source && selectedExecution.prompt_source !== 'inline' && (
              <div className="mb-4">
                <div
                  className="uppercase text-[10px] mb-2"
                  style={{ color: 'rgba(0, 255, 0, 0.5)' }}
                >
                  Prompt Source
                </div>
                <div
                  className="p-3 rounded"
                  style={{
                    background: 'rgba(0, 255, 0, 0.05)',
                    border: '1px solid rgba(0, 255, 0, 0.1)',
                  }}
                >
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>Source:</div>
                    <div style={{ color: selectedExecution.prompt_source === 'experiment' ? '#ffff00' : '#00ffff' }}>
                      {selectedExecution.prompt_source === 'experiment' ? 'A/B EXPERIMENT' : 'LIBRARY'}
                    </div>

                    {selectedExecution.prompt_name && (
                      <>
                        <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>Prompt:</div>
                        <div className="flex items-center space-x-2">
                          <span style={{ color: '#00ff00' }}>{selectedExecution.prompt_name}</span>
                          <a
                            href={`/prompts/${selectedExecution.prompt_id}`}
                            className="text-blue-400 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                            title="Edit prompt"
                          >
                            <i className="fa-solid fa-edit text-[8px]" />
                          </a>
                        </div>
                      </>
                    )}

                    {selectedExecution.prompt_version_number && (
                      <>
                        <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>Version:</div>
                        <div style={{ color: '#00ff00' }}>v{selectedExecution.prompt_version_number}</div>
                      </>
                    )}

                    {selectedExecution.experiment_name && (
                      <>
                        <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>Experiment:</div>
                        <div className="flex items-center space-x-2">
                          <span style={{ color: '#ffff00' }}>{selectedExecution.experiment_name}</span>
                          <a
                            href={`/experiments`}
                            className="text-blue-400 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                            title="View experiments"
                          >
                            <i className="fa-solid fa-flask text-[8px]" />
                          </a>
                        </div>
                      </>
                    )}

                    {selectedExecution.variant_name && (
                      <>
                        <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>Variant:</div>
                        <div style={{ color: '#ffff00' }}>{selectedExecution.variant_name}</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Inline prompt indicator */}
            {selectedExecution.prompt_source === 'inline' && (
              <div className="mb-4 text-[10px]" style={{ color: 'rgba(255, 165, 0, 0.7)' }}>
                <i className="fa-solid fa-info-circle mr-1" />
                Using inline prompt (not from library)
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-2 text-[10px]">
              <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>Model:</div>
              <div style={{ color: '#00ff00' }}>{selectedExecution.model || '---'}</div>
              <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>Duration:</div>
              <div style={{ color: '#00ff00' }}>{formatDuration(selectedExecution.duration_ms)}</div>
              <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>Tokens (in/out):</div>
              <div style={{ color: '#00ff00' }}>
                {formatTokens(selectedExecution.tokens_input, selectedExecution.tokens_output)}
              </div>
              <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>Status:</div>
              <div style={{ color: selectedExecution.status === 'error' ? '#ff4444' : '#00ff00' }}>
                {selectedExecution.status.toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Panel */}
      <div
        className={`${activeTab === 'prompts' ? 'w-[700px]' : 'w-[450px]'} h-full flex flex-col transition-all duration-300`}
        style={{
          background: 'rgba(0, 10, 0, 0.98)',
          borderLeft: '1px solid rgba(0, 255, 0, 0.3)',
          boxShadow: '-4px 0 20px rgba(0, 255, 0, 0.1)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}
        >
          <div className="flex items-center space-x-2">
            <div
              className="font-mono font-bold text-sm tracking-wider"
              style={{
                color: '#00ff00',
                textShadow: '0 0 10px rgba(0, 255, 0, 0.5)',
              }}
            >
              <i className="fa-solid fa-terminal mr-2" />
              GEEK MODE
            </div>
          </div>
          <button
            onClick={togglePanel}
            className="p-1 hover:opacity-70 transition-opacity"
            style={{ color: '#00ff00' }}
          >
            <i className="fa-solid fa-times" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div
          className="flex px-4 py-2"
          style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}
        >
          {(['executions', 'prompts', 'settings', 'aperture'] as GeekModeTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab)
                if (tab !== 'executions') setSelectedExecution(null)
              }}
              className="px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition-all mr-2"
              style={{
                background: activeTab === tab ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
                color: activeTab === tab ? '#00ff00' : 'rgba(0, 255, 0, 0.4)',
                border: `1px solid ${activeTab === tab ? 'rgba(0, 255, 0, 0.4)' : 'rgba(0, 255, 0, 0.1)'}`,
                borderRadius: '4px',
              }}
            >
              <i className={`fa-solid ${
                tab === 'executions' ? 'fa-list-check' :
                tab === 'prompts' ? 'fa-code' :
                tab === 'aperture' ? 'fa-satellite-dish' :
                'fa-cog'
              } mr-2`} />
              {tab === 'aperture' ? 'Aperture' : tab}
            </button>
          ))}
        </div>

        {/* Executions Tab Content */}
        {activeTab === 'executions' && (
          <>
            {/* Stats Bar */}
            {stats && (
              <div
                className="grid grid-cols-4 gap-2 px-4 py-2 font-mono text-[10px]"
                style={{
                  background: 'rgba(0, 255, 0, 0.05)',
                  borderBottom: '1px solid rgba(0, 255, 0, 0.1)',
                }}
              >
                <div className="text-center">
                  <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>CALLS</div>
                  <div style={{ color: '#00ff00' }}>{stats.total_executions}</div>
                </div>
                <div className="text-center">
                  <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>IN TOKENS</div>
                  <div style={{ color: '#00ff00' }}>{stats.total_tokens_input.toLocaleString()}</div>
                </div>
                <div className="text-center">
                  <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>OUT TOKENS</div>
                  <div style={{ color: '#00ff00' }}>{stats.total_tokens_output.toLocaleString()}</div>
                </div>
                <div className="text-center">
                  <div style={{ color: 'rgba(0, 255, 0, 0.5)' }}>TIME</div>
                  <div style={{ color: '#00ff00' }}>{formatDuration(stats.total_duration_ms)}</div>
                </div>
              </div>
            )}

            {/* Filter Tabs */}
            <div
              className="flex space-x-1 px-4 py-2"
              style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}
            >
              {(['all', 'completed', 'error'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className="px-3 py-1 rounded font-mono text-[10px] uppercase transition-all"
                  style={{
                    background: filter === f ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
                    color: filter === f ? '#00ff00' : 'rgba(0, 255, 0, 0.4)',
                    border: `1px solid ${filter === f ? 'rgba(0, 255, 0, 0.3)' : 'transparent'}`,
                  }}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Execution List */}
            <div ref={panelRef} className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-32 font-mono text-sm" style={{ color: 'rgba(0, 255, 0, 0.5)' }}>
              <i className="fa-solid fa-spinner fa-spin mr-2" />
              Loading...
            </div>
          ) : filteredExecutions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 font-mono text-xs" style={{ color: 'rgba(0, 255, 0, 0.4)' }}>
              <i className="fa-solid fa-robot text-2xl mb-2" />
              <div>No prompt executions yet</div>
              <div className="text-[10px] mt-1">Generate a document to see AI calls</div>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'rgba(0, 255, 0, 0.1)' }}>
              {filteredExecutions.map((exec, index) => (
                <button
                  key={exec.id}
                  onClick={() => setSelectedExecution(exec)}
                  className="w-full text-left px-4 py-3 transition-all hover:bg-opacity-100"
                  style={{
                    background: selectedExecution?.id === exec.id
                      ? 'rgba(0, 255, 0, 0.15)'
                      : index % 2 === 0
                      ? 'rgba(0, 255, 0, 0.02)'
                      : 'transparent',
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        {/* Status badge */}
                        <span
                          className="font-mono text-[10px] px-1.5 py-0.5 rounded"
                          style={{
                            background: exec.status === 'completed'
                              ? 'rgba(0, 255, 0, 0.2)'
                              : exec.status === 'error'
                              ? 'rgba(255, 0, 0, 0.2)'
                              : 'rgba(255, 255, 0, 0.2)',
                            color: exec.status === 'completed'
                              ? '#00ff00'
                              : exec.status === 'error'
                              ? '#ff4444'
                              : '#ffff00',
                          }}
                        >
                          {exec.status === 'completed' ? 'OK' : exec.status === 'error' ? 'ERR' : 'RUN'}
                        </span>

                        {/* A/B Experiment badge */}
                        {exec.experiment_id && (
                          <span
                            className="font-mono text-[10px] px-1.5 py-0.5 rounded"
                            style={{
                              background: 'rgba(255, 255, 0, 0.2)',
                              color: '#ffff00',
                            }}
                            title={`Experiment: ${exec.experiment_name || 'A/B Test'}`}
                          >
                            A/B
                          </span>
                        )}

                        {/* Library badge (only if from library and not in experiment) */}
                        {exec.prompt_source === 'library' && !exec.experiment_id && (
                          <span
                            className="font-mono text-[10px] px-1.5 py-0.5 rounded"
                            style={{
                              background: 'rgba(0, 255, 255, 0.2)',
                              color: '#00ffff',
                            }}
                            title={`From library: ${exec.prompt_name}`}
                          >
                            LIB
                          </span>
                        )}

                        <span
                          className="font-mono text-xs truncate"
                          style={{ color: '#00ff00' }}
                        >
                          {exec.section_name || 'Section'}
                        </span>
                      </div>
                      <div
                        className="font-mono text-[10px] truncate"
                        style={{ color: 'rgba(0, 255, 0, 0.5)' }}
                      >
                        {truncateText(exec.prompt_text, 80)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end ml-2 font-mono text-[9px]" style={{ color: 'rgba(0, 255, 0, 0.4)' }}>
                      <div>{formatTimestamp(exec.created_at)}</div>
                      <div>{formatDuration(exec.duration_ms)}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
          </>
        )}

        {/* Prompts Tab Content */}
        {activeTab === 'prompts' && (
          <div className="flex-1 overflow-hidden">
            <GeekModePromptEditor />
          </div>
        )}

        {/* Settings Tab Content */}
        {activeTab === 'settings' && (
          <div className="flex-1 overflow-y-auto p-4">
            {/* AI Model Selection */}
            <div className="mb-6">
              <div
                className="font-mono text-xs uppercase tracking-wider mb-3"
                style={{ color: '#00ff00' }}
              >
                <i className="fa-solid fa-robot mr-2" />
                AI Model Provider
              </div>

              {aiModelsLoading ? (
                <div className="flex items-center justify-center py-8 font-mono text-sm" style={{ color: 'rgba(0, 255, 0, 0.5)' }}>
                  <i className="fa-solid fa-spinner fa-spin mr-2" />
                  Loading models...
                </div>
              ) : aiModels ? (
                <div className="space-y-2">
                  {/* Current Model Info */}
                  <div
                    className="p-3 rounded mb-4"
                    style={{
                      background: 'rgba(0, 255, 0, 0.1)',
                      border: '1px solid rgba(0, 255, 0, 0.2)',
                    }}
                  >
                    <div className="font-mono text-[10px] uppercase" style={{ color: 'rgba(0, 255, 0, 0.5)' }}>
                      Current Model
                    </div>
                    <div className="font-mono text-sm" style={{ color: '#00ff00' }}>
                      {aiModels.models[aiModels.current]?.name || aiModels.current}
                    </div>
                    <div className="font-mono text-[10px] mt-1" style={{ color: 'rgba(0, 255, 0, 0.4)' }}>
                      {aiModels.models[aiModels.current]?.provider}
                    </div>
                  </div>

                  {/* Model List */}
                  <div className="space-y-2">
                    {Object.entries(aiModels.models).map(([key, model]) => (
                      <button
                        key={key}
                        onClick={() => {
                          if (model.available && key !== aiModels.current) {
                            updateModelMutation.mutate(key as AIModelProvider)
                          }
                        }}
                        disabled={!model.available || updateModelMutation.isPending}
                        className="w-full text-left p-3 rounded transition-all"
                        style={{
                          background: key === aiModels.current
                            ? 'rgba(0, 255, 0, 0.2)'
                            : model.available
                            ? 'rgba(0, 255, 0, 0.05)'
                            : 'rgba(255, 0, 0, 0.05)',
                          border: `1px solid ${
                            key === aiModels.current
                              ? 'rgba(0, 255, 0, 0.5)'
                              : model.available
                              ? 'rgba(0, 255, 0, 0.1)'
                              : 'rgba(255, 0, 0, 0.1)'
                          }`,
                          opacity: model.available ? 1 : 0.5,
                          cursor: model.available && key !== aiModels.current ? 'pointer' : 'default',
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-mono text-sm" style={{ color: model.available ? '#00ff00' : '#ff4444' }}>
                              {model.name}
                              {key === aiModels.current && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0, 255, 0, 0.3)' }}>
                                  ACTIVE
                                </span>
                              )}
                            </div>
                            <div className="font-mono text-[10px] mt-0.5" style={{ color: 'rgba(0, 255, 0, 0.5)' }}>
                              {model.provider}
                            </div>
                            <div className="font-mono text-[10px] mt-1" style={{ color: 'rgba(0, 255, 0, 0.4)' }}>
                              {model.description}
                            </div>
                          </div>
                          {!model.available && (
                            <div className="font-mono text-[10px] px-2 py-1 rounded" style={{ background: 'rgba(255, 0, 0, 0.2)', color: '#ff4444' }}>
                              NOT CONFIGURED
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Update Status */}
                  {updateModelMutation.isPending && (
                    <div className="flex items-center justify-center py-2 font-mono text-xs" style={{ color: '#ffff00' }}>
                      <i className="fa-solid fa-spinner fa-spin mr-2" />
                      Updating model...
                    </div>
                  )}
                  {updateModelMutation.isSuccess && (
                    <div className="flex items-center justify-center py-2 font-mono text-xs" style={{ color: '#00ff00' }}>
                      <i className="fa-solid fa-check mr-2" />
                      Model updated successfully
                    </div>
                  )}
                  {updateModelMutation.isError && (
                    <div className="flex items-center justify-center py-2 font-mono text-xs" style={{ color: '#ff4444' }}>
                      <i className="fa-solid fa-exclamation-triangle mr-2" />
                      Failed to update model
                    </div>
                  )}
                </div>
              ) : (
                <div className="font-mono text-xs" style={{ color: 'rgba(255, 0, 0, 0.7)' }}>
                  Failed to load AI models
                </div>
              )}
            </div>

            {/* Prompt Library Toggle */}
            <div className="mb-6 pt-4" style={{ borderTop: '1px solid rgba(0, 255, 0, 0.2)' }}>
              <div
                className="font-mono text-xs uppercase tracking-wider mb-3 flex items-center"
                style={{ color: '#00ff00' }}
              >
                <i className="fa-solid fa-book mr-2" />
                Prompt Pipeline
              </div>

              <div
                className="p-4 rounded"
                style={{
                  background: 'rgba(0, 255, 0, 0.05)',
                  border: '1px solid rgba(0, 255, 0, 0.2)',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[11px]" style={{ color: 'rgba(0, 255, 0, 0.7)' }}>
                    Use Prompt Library
                  </div>
                  <button
                    onClick={() => {
                      const next = !usePromptLibrary
                      setUsePromptLibrary(next)
                      localStorage.setItem('geekmode-use-prompt-library', next.toString())
                    }}
                    className="relative w-10 h-5 rounded-full transition-all"
                    style={{
                      background: usePromptLibrary
                        ? 'rgba(0, 255, 0, 0.4)'
                        : 'rgba(255, 255, 255, 0.1)',
                      border: `1px solid ${usePromptLibrary ? 'rgba(0, 255, 0, 0.6)' : 'rgba(255, 255, 255, 0.2)'}`,
                    }}
                  >
                    <div
                      className="absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all"
                      style={{
                        left: usePromptLibrary ? '22px' : '3px',
                        background: usePromptLibrary ? '#00ff00' : 'rgba(255, 255, 255, 0.4)',
                      }}
                    />
                  </button>
                </div>
                <div className="font-mono text-[10px]" style={{ color: 'rgba(0, 255, 0, 0.4)' }}>
                  {usePromptLibrary ? (
                    <>
                      <i className="fa-solid fa-check-circle mr-1" style={{ color: '#00ff00' }} />
                      Prompts resolved via library &amp; A/B experiments. Template prompts used as fallback.
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-file-alt mr-1" style={{ color: 'rgba(0, 255, 0, 0.5)' }} />
                      Using prompts embedded directly in the template. Faster, no library overhead.
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Workflow Mode Section */}
            <div className="mb-6 pt-4" style={{ borderTop: '1px solid rgba(0, 255, 0, 0.2)' }}>
              <div
                className="font-mono text-xs uppercase tracking-wider mb-3 flex items-center"
                style={{ color: '#00ff00' }}
              >
                <i className="fa-solid fa-flask mr-2" />
                Experimental Features
              </div>

              <div
                className="p-4 rounded"
                style={{
                  background: 'rgba(0, 255, 0, 0.05)',
                  border: '1px solid rgba(0, 255, 0, 0.2)',
                }}
              >
                <div className="font-mono text-[11px] mb-3" style={{ color: 'rgba(0, 255, 0, 0.7)' }}>
                  Workflow Mode
                </div>

                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setWorkflow('delivery')}
                    className="flex-1 px-4 py-2.5 rounded-lg font-mono text-sm font-medium transition-all"
                    style={{
                      background: !isPreSales
                        ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                        : 'rgba(0, 255, 0, 0.1)',
                      color: !isPreSales ? '#ffffff' : 'rgba(0, 255, 0, 0.5)',
                      border: !isPreSales
                        ? '1px solid #3b82f6'
                        : '1px solid rgba(0, 255, 0, 0.2)',
                      boxShadow: !isPreSales ? '0 0 15px rgba(59, 130, 246, 0.3)' : 'none',
                    }}
                  >
                    <i className="fa-solid fa-clipboard-check mr-2" />
                    Delivery
                  </button>
                  <button
                    onClick={() => setWorkflow('pre_sales')}
                    className="flex-1 px-4 py-2.5 rounded-lg font-mono text-sm font-medium transition-all"
                    style={{
                      background: isPreSales
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'rgba(0, 255, 0, 0.1)',
                      color: isPreSales ? '#ffffff' : 'rgba(0, 255, 0, 0.5)',
                      border: isPreSales
                        ? '1px solid #34d399'
                        : '1px solid rgba(0, 255, 0, 0.2)',
                      boxShadow: isPreSales ? '0 0 15px rgba(16, 185, 129, 0.3)' : 'none',
                    }}
                  >
                    <i className="fa-solid fa-handshake mr-2" />
                    Pre-Sales
                  </button>
                </div>

                <div className="font-mono text-[10px]" style={{ color: 'rgba(0, 255, 0, 0.5)' }}>
                  {isPreSales ? (
                    <>
                      <i className="fa-solid fa-check-circle mr-1" style={{ color: '#10b981' }} />
                      SOW & WBS creation mode. Navigation and templates filtered for pre-sales workflows.
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check-circle mr-1" style={{ color: '#3b82f6' }} />
                      Standard delivery workflow. WAF/CAF assessments and remediation tracking.
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div
              className="p-3 rounded"
              style={{
                background: 'rgba(0, 255, 0, 0.05)',
                border: '1px solid rgba(0, 255, 0, 0.1)',
              }}
            >
              <div className="font-mono text-[10px]" style={{ color: 'rgba(0, 255, 0, 0.6)' }}>
                <i className="fa-solid fa-info-circle mr-2" />
                Changes persist until server restart. Update environment variables for permanent changes.
              </div>
            </div>
          </div>
        )}

        {activeTab === 'aperture' && (
          <ApertureStatusTab />
        )}

        {/* Footer */}
        <div
          className="px-4 py-2 font-mono text-[9px] text-center"
          style={{
            borderTop: '1px solid rgba(0, 255, 0, 0.1)',
            color: 'rgba(0, 255, 0, 0.3)',
          }}
        >
          {activeTab === 'executions'
            ? 'Tip: Enter the Konami Code to unlock GEEK MODE'
            : activeTab === 'prompts'
            ? 'Edit prompts to tune AI document generation'
            : 'Configure AI model provider for document generation'
          }
        </div>
      </div>
    </div>
  )
}
